import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { ControllerForViewBase } from '../framework/controller-for-view-base';
import { PageContainerService } from '../../services';
import { AddUpdateSupportTicketCommandRequest, GetSupportTicketCommandRequest, GetSupportTicketCommandResponse } from '../../data/CommandServiceDtos.generated';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

export interface SupportTicketData {
  mode: 'create' | 'edit' | 'view';
  ticket?: any;
}

@Component({
  standalone: false,
  selector: 'app-support-ticket-form',
  templateUrl: './support-ticket-form.component.html',
  styleUrls: ['./support-ticket-form.component.scss'],
})
export class SupportTicketFormComponent extends ControllerForViewBase implements OnInit {
  faTimes = faTimes;
  ticket: any = { Title: '', Description: '', Type: 3, ImageUrl: null, Status: 0 };
  saving = false;
  file: File | null = null;
  previewUrl: string | null = null; // image shown below (set or loaded)
  stagedFile: File | null = null; // file selected but not yet set
  stagedPreviewUrl: string | null = null; // preview for staged file

  constructor(
    public dialogRef: MatDialogRef<SupportTicketFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SupportTicketData,
    public pcs: PageContainerService
  ) {
    super(pcs);
    if (data && data.ticket) {
      this.ticket = { ...data.ticket };
    }
  }

  async loadTicket() {
    var request = new GetSupportTicketCommandRequest();
    request.Id = this.ticket.SupportTicketId;
    const getCommandResponse: GetSupportTicketCommandResponse = await this.pcs.apiService.GetSupportTicket(request);
    if (getCommandResponse.SupportTickets != null && getCommandResponse.SupportTickets.length > 0)
      this.ticket = getCommandResponse.SupportTickets[0];
    // if the loaded ticket contains an image, show preview and expand dialog
    if (this.ticket) {
      if (this.ticket.ImageBase64) {
        this.previewUrl = this.ticket.ImageBase64;
      } else if (this.ticket.ImageUrl) {
        this.previewUrl = this.ticket.ImageUrl;
      }
    }

  }
  ngOnInit(): void {
    if (this.ticket && this.ticket.SupportTicketId > 0 && this.data.mode === 'view') {
      this.loadTicket();
    }
    // leave initial sizing to content; we'll resize when an image preview is applied
  }

  isReadOnly() {
    return this.data && this.data.mode === 'view';
  }

  async save() {
    if (this.isReadOnly()) return;
    this.saving = true;
    try {
      // const form = new FormData();
      // form.append('Title', this.ticket.Title || '');
      // form.append('Description', this.ticket.Description || '');
      // form.append('Type', String(this.ticket.Type));
      // form.append('Status', String(this.ticket.Status));        

      let commandRequest = new AddUpdateSupportTicketCommandRequest();
      if (this.file) {
        commandRequest.supportTicket.ImageBase64 = await this.fileToBase64(this.file);
      }
      commandRequest.supportTicket.Title = this.ticket.Title;
      commandRequest.supportTicket.Description = this.ticket.Description;
      commandRequest.supportTicket.Type = this.ticket.Type;
      commandRequest.supportTicket.Status = this.ticket.Status;

      var response = await this.pcs.apiService.AddUpdateSupportTicket(commandRequest);
      if (response && response.SupportTicket && response.SupportTicket.SupportTicketId) {
        this.pcs.notificationService.success('Support ticket added successfully.', '', {
          timeOut: 3000
        });
      }

      this.dialogRef.close(true);
    } catch (e) {
      this.pcs.notificationService.danger('Error saving support ticket.', '');
      this.saving = false;
    }
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  }

  close() {
    this.dialogRef.close(false);
  }

  onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input || !input.files || input.files.length === 0) return;
    // stage the file; don't apply until user clicks Set
    this.stagedFile = input.files[0];
    try {
      // create temporary preview for staged file
      if (this.stagedPreviewUrl) {
        try { URL.revokeObjectURL(this.stagedPreviewUrl); } catch (_) { }
      }
      this.stagedPreviewUrl = URL.createObjectURL(this.stagedFile);
    } catch (e) {
      this.stagedPreviewUrl = null;
    }
  }

  clearFile() {
    if (this.stagedPreviewUrl) {
      try { URL.revokeObjectURL(this.stagedPreviewUrl); } catch (_) { }
    }

    if (this.previewUrl && this.previewUrl.startsWith('blob:')) {
      try { URL.revokeObjectURL(this.previewUrl); } catch (_) { }
    }

    this.file = null;
    this.previewUrl = null;
    this.stagedFile = null;
    this.stagedPreviewUrl = null;

    // ✅ Clear Base64 (AngularJS equivalent)
    this.ticket.ImageBase64 = null;
    // ensure dialog width remains 50vw and height shrinks to content
  }


  clearStaged() {
    if (this.stagedPreviewUrl) { try { URL.revokeObjectURL(this.stagedPreviewUrl); } catch (_) { } }
    this.stagedFile = null;
    this.stagedPreviewUrl = null;
  }

  // apply staged file as the selected file to be saved and shown
  async setFile() {
    if (!this.stagedFile) return;

    // File size check (8 MB)
    if (this.stagedFile.size > 8 * 1024 * 1024) {
      await this.pcs.alert('File size exceeds 8 MB. Please import a smaller file.', 'File too large', 'warning');
      this.clearFile();
      return;
    }

    // Revoke previous preview
    if (this.previewUrl && this.previewUrl.startsWith('blob:')) {
      try { URL.revokeObjectURL(this.previewUrl); } catch (_) { }
    }

    this.file = this.stagedFile;
    this.previewUrl = this.stagedPreviewUrl;

    // ✅ Convert to Base64 (same as AngularJS)
    try {
      this.ticket.ImageBase64 = await this.readFileAsBase64(this.file);
    } catch {
      await this.pcs.alert('Unable to load image.', 'Error', 'error');
      this.clearFile();
      return;
    }

    // Clear staged references
    this.stagedFile = null;
    this.stagedPreviewUrl = null;
    // ensure dialog width remains 50vw and height grows to fit image
  }


  private readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => reject('Failed to read file');
      reader.readAsDataURL(file);
    });
  }

}
